// dependency-graph-ignore-file

import {
  analyticsClient,
  getDatabaseContainerType,
  SendDatabaseDeleteEntryEventSources
} from '@databases/analytics';
import { copyToClipboard } from '@databases/copy-paste/copy-helper';
import {
  InlineRovoPromptTriggerSource,
  openInlineRovoPrompt
} from '@databases/database-ai-first-creation-state/inline-rovo-prompt-state';
import { getBaseUrl } from '@databases/database-state';
import {
  DatabaseTraceAction,
  DatabaseTraceSource,
  traceDatabaseAction
} from '@databases/frontend-metrics/perf-tracing';
import { type PanelManager } from '@databases/panel-types';
import {
  doClearEntireEntriesWithIds,
  doDeleteEntriesWithIds,
  doDuplicateEntireEntriesWithIds,
  doInsertEntriesWithIds,
  doMoveEntriesWithIds
} from '@databases/redux/contextual-menu/actions';
import { appStore } from '@databases/redux/store';
import {
  type SelectionsApiContract,
  type DatabaseAdapter
} from '@databases/selections-api-types';
import { type DatabaseView } from '@databases/types/database/view';
import {
  getDatabaseLinkUrlWithQueryParams,
  getDatabasePath
} from '@databases/utils/urls';

import { type PositionType, type RowActionsAPI } from '../types.ts';
import { getSelectedEntryIds } from '../utils/resolve-row-selection.ts';

/**
 * Builds the action handlers for entry-level operations in the action bar.
 */
export const buildRowActions = ({
  contentId,
  selectionsApi,
  view,
  spaceKey,
  panelManager,
  isLocked
}: {
  contentId: string;
  selectionsApi: SelectionsApiContract;
  view: DatabaseView;
  databaseAdapter: DatabaseAdapter;
  spaceKey: string;
  panelManager: PanelManager;
  isLocked: boolean;
}): RowActionsAPI => {
  const dispatch = appStore.dispatch;

  /**
   * Clears the content of all selected entries without deleting them.
   */
  const handleClearEntries = () => {
    const entryIds = getSelectedEntryIds(selectionsApi.getResolvedSelection());

    if (entryIds.length === 0) {
      return;
    }

    traceDatabaseAction(
      { action: DatabaseTraceAction.EntryClear, source: DatabaseTraceSource.ActionBar },
      () => {
        dispatch(doClearEntireEntriesWithIds(contentId, entryIds));
      }
    );
  };

  /**
   * Permanently deletes all selected entries and clears the selection.
   */
  const handleDeleteEntries = async () => {
    const entryIds = getSelectedEntryIds(selectionsApi.getResolvedSelection());

    if (entryIds.length === 0) {
      return;
    }

    await analyticsClient.sendDeleteEntryEvent({
      contentId: contentId,
      databaseContainer: getDatabaseContainerType(),
      source: SendDatabaseDeleteEntryEventSources.ACTION_BAR
    });

    traceDatabaseAction(
      { action: DatabaseTraceAction.EntryDelete, source: DatabaseTraceSource.ActionBar },
      () => {
        dispatch(doDeleteEntriesWithIds(contentId, entryIds));

        selectionsApi.clearPreviewIntent();
        selectionsApi.deselectAll();
      }
    );
  };

  /**
   * Duplicates all selected entries and selects the newly created copies.
   */
  const handleDuplicateEntries = () => {
    const entryIds = getSelectedEntryIds(selectionsApi.getResolvedSelection());

    if (entryIds.length === 0) {
      return;
    }

    traceDatabaseAction(
      {
        action: DatabaseTraceAction.EntryDuplicate,
        source: DatabaseTraceSource.ActionBar
      },
      () => {
        dispatch(doDuplicateEntireEntriesWithIds(contentId, entryIds));
      }
    );
  };

  /**
   * Inserts new entries before or after the selected entries and selects them.
   * When `entryIds` is provided it is used directly; otherwise the IDs are
   * resolved from the current selection state.
   */
  const handleInsertEntries = async (position: PositionType, entryIds?: string[]) => {
    const ids = entryIds ?? getSelectedEntryIds(selectionsApi.getResolvedSelection());

    if (ids.length === 0) {
      return;
    }

    await analyticsClient.sendInsertRowEvent({
      contentId: contentId,
      databaseContainer: getDatabaseContainerType()
    });

    traceDatabaseAction(
      {
        action:
          position === 'before'
            ? DatabaseTraceAction.EntryInsertBefore
            : DatabaseTraceAction.EntryInsertAfter,
        source: DatabaseTraceSource.ActionBar
      },
      () => {
        dispatch(
          doInsertEntriesWithIds(contentId, view.settings, position === 'before', ids)
        );
      }
    );
  };

  /**
   * Moves the selected entries up or down by one position in the view.
   */
  const handleMoveEntries = (position: PositionType) => {
    const entryIds = getSelectedEntryIds(selectionsApi.getResolvedSelection());

    if (entryIds.length === 0) {
      return;
    }

    traceDatabaseAction(
      {
        action:
          position === 'before'
            ? DatabaseTraceAction.EntryMoveUp
            : DatabaseTraceAction.EntryMoveDown,
        source: DatabaseTraceSource.ActionBar
      },
      () => {
        dispatch(doMoveEntriesWithIds(contentId, position === 'before', entryIds));
      }
    );
  };

  /**
   * Opens the inline Rovo AI prompt for the selected entry.
   */
  const handleAskRovo = () => {
    return openInlineRovoPrompt(InlineRovoPromptTriggerSource.TableEntry);
  };

  /**
   * Copies a shareable link for a single selected entry to the clipboard.
   */
  const handleCopyEntryLink = (setIsCopyLinkCopied: (isCopied: boolean) => void) => {
    const entryIds = getSelectedEntryIds(selectionsApi.getResolvedSelection());

    if (entryIds.length !== 1) {
      return;
    }

    const entryId = entryIds[0];
    const baseUrl = getBaseUrl();
    const databasePath = getDatabasePath({ contentId, spaceKey });

    if (!baseUrl || !databasePath) {
      return;
    }

    const entryLinkCopyUrl = getDatabaseLinkUrlWithQueryParams({
      baseUrl,
      databasePath,
      contentId,
      entryId,
      viewId: view.id
    });

    copyToClipboard(entryLinkCopyUrl.toString(), 'text/plain');
    setIsCopyLinkCopied(true);

    setTimeout(() => setIsCopyLinkCopied(false), 3000);
  };

  /**
   * Opens the entry detail panel for a single selected entry.
   */
  const handleExpandEntry = () => {
    const entryIds = getSelectedEntryIds(selectionsApi.getResolvedSelection());

    if (entryIds.length !== 1) {
      return;
    }

    const entryId = entryIds[0];

    panelManager.showPanel('EntryViewPanel', {
      entryId,
      isReadonly: isLocked
    });
  };

  const handleHoverDelete = () => {
    selectionsApi.previewDeleteIntent();
  };

  const handleClearHoverDelete = () => {
    selectionsApi.clearPreviewIntent();
  };

  return {
    handleAskRovo,
    handleClearEntries,
    handleClearHoverDelete,
    handleCopyEntryLink,
    handleDeleteEntries,
    handleDuplicateEntries,
    handleExpandEntry,
    handleHoverDelete,
    handleInsertEntries,
    handleMoveEntries
  };
};
